import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Boxes, Plug, ShieldCheck, Handshake, Building2, MapPin } from "lucide-react";
import { Container, SectionHead, Eyebrow } from "@/components/common/Section";
import { Reveal } from "@/lib/anim";
import { CASE_STUDIES } from "@/lib/content";
import ArchitectureDiagram from "@/components/visuals/ArchitectureDiagram";

/* ---------- Section 19: Engineering ---------- */

export function EngineeringSection() {
  return (
    <section data-testid="engineering-section" className="border-y border-[#E2E8F0] bg-white py-20 lg:py-28">
      <Container>
        <SectionHead
          center
          eyebrow="Engineered for Scale"
          title="Engineered for scale. Designed for change."
          copy="An API-first, event-driven architecture that keeps payment platforms adaptable as rails, partners and regulations evolve."
        />
        <div className="mt-14">
          <ArchitectureDiagram />
        </div>
      </Container>
    </section>
  );
}

/* ---------- Section 20: Selected Work ---------- */

export function SelectedWorkSection() {
  return (
    <section data-testid="selected-work-section" className="py-20 lg:py-28">
      <Container>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead eyebrow="Selected Work" title="Technology in production." />
          <Reveal delay={100}>
            <Link
              to="/work"
              data-testid="selected-work-view-all"
              className="group inline-flex items-center gap-2 text-[15px] font-semibold text-[#1D305B] transition-colors hover:text-[#2563EB]"
            >
              View all work
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {CASE_STUDIES.map((c, i) => (
            <Reveal
              key={c.name}
              delay={(i % 3) * 80}
              data-testid={`case-card-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="group flex flex-col rounded-2xl border border-[#E2E8F0] bg-white p-7 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_48px_-20px_rgba(15,23,42,0.18)]"
            >
              <div className="flex items-center justify-between gap-4">
                <span className="btn-brand flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] text-sm font-bold text-white">
                  {c.name[0]}
                </span>
                <span className="text-right text-[11px] font-semibold uppercase leading-snug tracking-[0.14em] text-[#0EA5A4]">{c.category}</span>
              </div>
              <h3 className="mt-5 text-xl font-semibold text-[#1D305B]">{c.name}</h3>
              <p className="mt-2.5 text-[15px] leading-relaxed text-[#64748B]">{c.desc}</p>
              <Link
                to="/work"
                data-testid={`case-card-${c.name.toLowerCase().replace(/\s+/g, "-")}-link`}
                className="group/link mt-auto inline-flex items-center gap-2 pt-6 text-sm font-semibold text-[#1D305B] transition-colors hover:text-[#2563EB]"
              >
                View engagement
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1" aria-hidden="true" />
              </Link>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ---------- Section 21: Why iJeeva ---------- */

const PILLARS = [
  {
    icon: <BookOpen className="h-5 w-5" />,
    title: "Domain Expertise",
    desc: "Deep understanding of payments, banking and financial technology.",
  },
  {
    icon: <Boxes className="h-5 w-5" />,
    title: "Product Mindset",
    desc: "We build platforms, not just projects.",
  },
  {
    icon: <Plug className="h-5 w-5" />,
    title: "Integration Expertise",
    desc: "Connect banks, payment providers, financial services and third-party systems.",
  },
  {
    icon: <ShieldCheck className="h-5 w-5" />,
    title: "Security First",
    desc: "Security and operational controls designed into the architecture.",
  },
  {
    icon: <Handshake className="h-5 w-5" />,
    title: "Long-Term Partnership",
    desc: "We work alongside clients from strategy and implementation through continuous evolution.",
  },
];

export function WhyIjeevaSection() {
  return (
    <section data-testid="why-ijeeva-section" className="border-y border-[#E2E8F0] bg-white py-20 lg:py-28">
      <Container className="grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <SectionHead
              eyebrow="Why iJeeva"
              title={<>Deep payments expertise. <span className="text-brand-gradient">Practical engineering.</span></>}
              copy="Five principles shape every platform, integration and engagement we take on."
            />
          </div>
        </div>
        <div className="lg:col-span-7">
          {PILLARS.map((p, i) => (
            <Reveal
              key={p.title}
              delay={i * 60}
              data-testid={`why-pillar-${i}`}
              className={`flex gap-5 border-t border-[#E2E8F0] py-7 ${i === PILLARS.length - 1 ? "border-b" : ""}`}
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border border-[#E2E8F0] bg-[#F8FAFC] text-[#2563EB]">
                {p.icon}
              </span>
              <div>
                <h3 className="text-lg font-semibold text-[#1D305B]">{p.title}</h3>
                <p className="mt-1.5 text-[15px] leading-relaxed text-[#64748B]">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ---------- Section 22: About ---------- */

const TEAM_CREDENTIALS = [
  "Nokia Money",
  "NPCI / UPI",
  "Core Banking System integrations",
  "Digital banking",
  "Financial inclusion",
];

export function AboutSection() {
  return (
    <section data-testid="about-section" className="py-20 lg:py-28">
      <Container className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <Eyebrow>About iJeeva</Eyebrow>
          <h2 className="mt-4 text-[32px] font-semibold leading-[1.12] tracking-tight text-[#1D305B] sm:text-4xl lg:text-[44px]">
            Experience that <span className="text-brand-gradient">understands payments.</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#64748B] sm:text-lg">
            Founded in 2015, iJeeva is a Bengaluru-based technology company specializing in enterprise payment
            platforms, gateways and financial technology solutions.
          </p>
          <p className="mt-4 text-base leading-relaxed text-[#64748B] sm:text-lg">
            Our strength comes from combining deep payments expertise with hands-on engineering. We work closely with
            businesses, fintechs and financial institutions to design, build and evolve technology that powers
            real-world financial experiences.
          </p>
          <h3 className="mt-10 text-xl font-semibold leading-snug tracking-tight text-[#1D305B] sm:text-2xl">
            Built by people who have been at the forefront of banking and payments.
          </h3>
          <p className="mt-4 text-base leading-relaxed text-[#64748B]">
            Our founding team brings extensive experience across banking, payments and regulatory compliance, with
            hands-on experience in building and scaling financial technology platforms.
          </p>
        </Reveal>
        <Reveal delay={150}>
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-8 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2563EB]">Founding team experience</p>
            <ul className="mt-6 divide-y divide-[#F1F5F9]">
              {TEAM_CREDENTIALS.map((c) => (
                <li key={c} className="flex items-center gap-3.5 py-4">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0EA5A4]" aria-hidden="true" />
                  <span className="text-[15px] font-medium text-[#1D305B]">{c}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-2.5">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-2 text-[13px] font-medium text-[#334155]">
                <Building2 className="h-3.5 w-3.5 text-[#2563EB]" aria-hidden="true" />
                Founded 2015
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-2 text-[13px] font-medium text-[#334155]">
                <MapPin className="h-3.5 w-3.5 text-[#2563EB]" aria-hidden="true" />
                Bengaluru, India
              </span>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}
